"use client";

import type { ReactNode } from "react";
import type { AnchoredSurface } from "@/lib/monetization/anchors";
import { useResolvedAnchor } from "@/lib/monetization/use-ad-guardrails";

/**
 * Payload-tunable placement anchor host (PR-5, freemium-monetization-
 * remediation plan § 4b). A thin gate around a monetized unit (`<NativeAd>` /
 * `<FeedInlineAd>`): renders `children` only when the surface's resolved
 * placement anchor (`surfaces.<surface>.anchor` in the `monetization-settings`
 * PostHog payload, see `@/lib/monetization/anchors`) equals THIS instance's
 * `anchor`. Every registered candidate anchor for a surface gets its own
 * `<AnchorSlot>` at its DOM mount point; exactly one resolves truthy at a
 * time (`useResolvedAnchor` always returns a single value, falling back to
 * the surface's default anchor on an invalid/absent config) — so flipping the
 * flag payload moves the unit between mount points with zero deploy, and an
 * invalid anchor value can never blank every slot.
 */
export function AnchorSlot({
	surface,
	anchor,
	className,
	children,
}: {
	surface: AnchoredSurface;
	anchor: string;
	className?: string;
	children: ReactNode;
}) {
	const resolvedAnchor = useResolvedAnchor(surface);
	if (resolvedAnchor !== anchor) return null;
	return className ? <div className={className}>{children}</div> : children;
}
